import { h } from '../ui/icons';
import type { EngraveSession } from './EngraveSession';
import { ew, FaceAccordion, fontPicker, recapList, resetButton, symbolPalette, undoButton, type Part, type TextField } from './parts';
import { isEditable, type EditableSlot } from './state';
import { SYMBOL_COUNT } from './symbols';

/**
 * A110Z — desktop « Gravure » tab of the right column (design handoff). The temples themselves are in the band
 * under the face (`EngraveBand`); clicking a zone there opens its panel here and focuses its field.
 *
 * A201 (David, 8 octobre) : 1 · Branches — four panels, one per temple and face, only one open (the logo of the
 * inside left with its colour, size and place; each text with its own colour, height and place, quick positions
 * and the fine settings under « Ajuster »). A204: summary, then fonts inside the bottom of the menu.
 */
export class EngravePanel {
  readonly root = h('div', 'mt-view mt-eng-view');
  private readonly accordion: FaceAccordion;
  private readonly parts: Part[] = [];
  private readonly palette = symbolPalette(g => this.paletteField?.insert(g));
  private paletteField: TextField | null = null;
  private readonly unsubscribe: () => void;

  constructor(private readonly session: EngraveSession) {
    const w = ew();
    this.root.dataset.view = 'engrave';
    this.root.setAttribute('role', 'group');
    this.root.setAttribute('aria-label', w.title);

    // 1 · Branches
    const texts = h('section', 'mt-eng-sec mt-eng-sec--texts');
    texts.append(h('h3', 'mt-csec-title', `1 · ${w.branches}`));
    this.accordion = new FaceAccordion(session, { desktop: true, onSymbols: f => this.togglePalette(f) });
    texts.append(this.accordion.root, h('p', 'mt-eng-note mt-eng-allowed', w.allowed(SYMBOL_COUNT)));
    this.parts.push(this.accordion);
    this.palette.root.classList.add('mt-eng-symbols--pop');
    this.palette.root.hidden = true;

    // Keep typography inside the desktop menu so the face and temples stay visible.
    const fonts = h('section', 'mt-eng-sec mt-eng-sec--font mt-eng-sec--font-inline');
    const picker = fontPicker(session, 'grid');
    fonts.append(h('h3', 'mt-csec-title', `3 · ${w.font}`), picker.root);
    this.parts.push(picker);

    // 2 · Récapitulatif
    const recap = h('section', 'mt-eng-sec mt-eng-sec--recap');
    const recapHead = h('div', 'mt-csec-head');
    const undo = undoButton(session);
    const actions = h('div', 'mt-eng-recap-actions');
    actions.append(undo.root, resetButton(session));
    recapHead.append(h('h3', 'mt-csec-title', `2 · ${w.recap}`), actions);
    const list = recapList(session);
    recap.append(recapHead, list.root);
    this.parts.push(list, undo);

    this.root.append(texts, recap, fonts);
    this.unsubscribe = session.onChange(event => {
      if (event === 'state' || event === 'focus' || event === 'frame' || event === 'image' || event === 'warn' || event === 'face') this.refresh();
      // A zone clicked in the band: the cursor goes to its field (only while this panel is on screen).
      if (event === 'focus' && session.active && isEditable(session.active) && this.visible()) this.focusField(session.active, true);
    });
    this.refresh();
  }

  private visible(): boolean {
    return this.root.isConnected && !this.root.hidden && this.root.offsetParent !== null;
  }

  /** The tab opened: fonts are fetched now (not before); the inside right panel opens when none is. */
  onShow(): void {
    this.session.prepareFonts();
    if (!this.session.active) this.session.setActive('ir');
  }

  onHide(): void {
    this.closePalette();
  }

  /** From the temple band: puts the cursor in the field of that slot. */
  focusField(slot: EditableSlot, select: boolean): void {
    const field = this.accordion.fields.get(slot);
    if (!field) return;
    if (document.activeElement !== field.input && select) field.focus();
  }

  private togglePalette(field: TextField): void {
    if (this.paletteField === field && !this.palette.root.hidden) { this.closePalette(); return; }
    this.paletteField = field;
    for (const f of this.accordion.fields.values()) f.symbols.setAttribute('aria-expanded', String(f === field));
    field.root.after(this.palette.root);
    this.palette.root.hidden = false;
    if (field.slot) this.session.setActive(field.slot);
  }

  private closePalette(): void {
    this.palette.root.hidden = true;
    this.paletteField = null;
    for (const f of this.accordion.fields.values()) f.symbols.setAttribute('aria-expanded', 'false');
  }

  refresh(): void {
    for (const p of this.parts) p.refresh();
    // The palette follows its field: closed when that panel is.
    if (this.paletteField && this.paletteField.slot !== this.session.active) this.closePalette();
  }

  destroy(): void {
    this.unsubscribe();
  }
}
